import React, { useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSiteStore } from '@core/site/useSiteStore';
import { useClientStore } from '@core/client/useClientStore';
import {
  Address,
  ClassificationGroup,
  LatLng,
  SiteInput,
} from '@core/generated/gql-types';
import {
  buildFriendlyAddressFromParts,
  generateFriendlyAddress,
} from '@core/util/google-places.service';
import useSiteAssignment, {
  getUserDisplayName,
} from '@/hooks/useSiteAssignment';
import AddressForm from '@/components/forms/AddressForm';
import Button from '@/components/Button';

type Props = {
  isOpen: boolean;
  clientId: string;
  onClose: () => void;
  onSaved?: () => void;
};

const EMPTY_ADDRESS: Address = {
  addressLine1: '',
  addressLine2: '',
  addressFriendly: '',
  suburb: '',
  state: '',
  postcode: '',
  countryLabel: '',
  countryValue: '',
};

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Site name is required')
    .max(100, 'Name must be at most 100 characters'),
  address: Yup.object()
    .nullable()
    .test('address-required', 'Address is required', (value) => {
      return value !== null && value !== undefined;
    })
    .shape({
      addressLine1: Yup.string().required('Address is required'),
      suburb: Yup.string().required('Suburb is required'),
      state: Yup.string().required('State is required'),
      postcode: Yup.string().required('Postcode is required'),
      countryLabel: Yup.string().required('Country is required'),
    }),
});

const SiteModal = ({ isOpen, clientId, onClose, onSaved }: Props) => {
  const { createSite, setAccountsForSite } = useSiteStore();
  const { fetchUsersByClient, fetchInvitesForClient } = useClientStore();

  const [isManualAddressEntry, setIsManualAddressEntry] = useState(false);

  const {
    siteAssignmentOptions,
    selectedUserIds,
    selectedInviteIds,
    selectedOptionsCount,
    toggleSelectedUser,
    toggleSelectedInvite,
    selectAllAssignable,
    clearSelection,
  } = useSiteAssignment({ clientId });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: '',
      classifications: [] as ClassificationGroup[],
      address: EMPTY_ADDRESS,
      latLng: null as LatLng | null,
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, setStatus, resetForm }) => {
      setSubmitting(true);
      setStatus(undefined);

      try {
        if (!clientId) {
          setStatus('A client is required to save a site');
          setSubmitting(false);
          return;
        }

        const payload: SiteInput = {
          name: values.name,
          clientId,
          address: values.address
            ? {
                addressFriendly: values.latLng
                  ? values.address.addressFriendly ||
                    generateFriendlyAddress(values.address)
                  : buildFriendlyAddressFromParts(values.address),
                addressLine1: values.address?.addressLine1 || '',
                addressLine2: values.address?.addressLine2 || '',
                suburb: values.address?.suburb || '',
                state: values.address?.state || '',
                postcode: values.address?.postcode || '',
                countryLabel: values.address?.countryLabel || '',
                countryValue: values.address?.countryValue || '',
              }
            : undefined,
          latLng: values.latLng
            ? { lat: values.latLng.lat || 0, lng: values.latLng.lng || 0 }
            : undefined,
          customAttributes: [],
        };

        const createResult = await createSite(payload);
        if (!createResult.success || !createResult.site) {
          setStatus(createResult.error ?? 'Failed to create site');
          return;
        }

        if (selectedUserIds.length > 0 || selectedInviteIds.length > 0) {
          const usersResult = await setAccountsForSite(
            createResult.site.id,
            selectedUserIds,
            selectedInviteIds,
          );
          if (!usersResult.success) {
            setStatus(usersResult.error ?? 'Failed to assign site accounts');
            return;
          }

          await Promise.all([
            fetchUsersByClient(clientId),
            fetchInvitesForClient(clientId),
          ]);
        }

        resetForm();
        clearSelection();

        setIsManualAddressEntry(false);
        onSaved?.();
        onClose();
      } catch (error) {
        setStatus(
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleClose = () => {
    if (formik.isSubmitting) return;
    formik.resetForm();
    clearSelection();
    setIsManualAddressEntry(false);
    onClose();
  };

  const addressFieldErrors = useMemo(() => {
    const errors = formik.errors.address;
    if (!errors || typeof errors === 'string') return null;
    return errors;
  }, [formik.errors.address]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="flex max-h-[90vh] w-[600px] flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex flex-shrink-0 items-center justify-between px-8 pt-8 pb-6">
          <h3 className="text-2xl font-bold text-gray-800">Add Site</h3>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Close add site modal"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form
          onSubmit={formik.handleSubmit}
          className="flex min-h-0 flex-1 flex-col"
        >
          <div className="flex-1 space-y-6 overflow-y-auto px-8 pb-6">
            {formik.status && (
              <div className="rounded-md border border-red-200 bg-red-50 p-4">
                <p className="text-sm text-red-600">{formik.status}</p>
              </div>
            )}
            <div>
              <label
                className="mb-1 block text-sm font-medium text-gray-700"
                htmlFor="site-name"
              >
                Site Name *
              </label>
              <input
                type="text"
                id="site-name"
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`w-full rounded-md border bg-white px-4 py-2 focus:border-blue-500 focus:ring-blue-500 ${
                  formik.touched.name && formik.errors.name
                    ? 'border-red-300'
                    : 'border-gray-300'
                }`}
              />
              {formik.touched.name && formik.errors.name && (
                <div className="mt-1 text-sm text-red-600">
                  {formik.errors.name}
                </div>
              )}
            </div>

            <div>
              <AddressForm
                onAddressChange={(address) =>
                  formik.setFieldValue('address', address)
                }
                onCoordinatesChange={(coordinates) =>
                  formik.setFieldValue('latLng', coordinates)
                }
                initialAddress={formik.values.address}
                onManualEntryChange={setIsManualAddressEntry}
              />
              {!isManualAddressEntry && (
                <>
                  {formik.touched.address &&
                    typeof formik.errors.address === 'string' && (
                      <div className="mt-1 text-sm text-red-600">
                        {formik.errors.address}
                      </div>
                    )}
                  {formik.touched.address &&
                    addressFieldErrors?.addressLine1 && (
                      <div className="mt-1 text-sm text-red-600">
                        {addressFieldErrors.addressLine1}
                      </div>
                    )}
                </>
              )}
              {isManualAddressEntry && (
                <>
                  {formik.touched.address?.addressLine1 &&
                    addressFieldErrors?.addressLine1 && (
                      <div className="mt-1 text-sm text-red-600">
                        {addressFieldErrors.addressLine1}
                      </div>
                    )}
                  {formik.touched.address?.suburb &&
                    addressFieldErrors?.suburb && (
                      <div className="mt-1 text-sm text-red-600">
                        {addressFieldErrors.suburb}
                      </div>
                    )}
                  {formik.touched.address?.state &&
                    addressFieldErrors?.state && (
                      <div className="mt-1 text-sm text-red-600">
                        {addressFieldErrors.state}
                      </div>
                    )}
                  {formik.touched.address?.postcode &&
                    addressFieldErrors?.postcode && (
                      <div className="mt-1 text-sm text-red-600">
                        {addressFieldErrors.postcode}
                      </div>
                    )}
                  {formik.touched.address?.countryLabel &&
                    addressFieldErrors?.countryLabel && (
                      <div className="mt-1 text-sm text-red-600">
                        {addressFieldErrors.countryLabel}
                      </div>
                    )}
                </>
              )}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Users
              </label>
              <p className="mb-2 text-sm text-gray-500">
                Choose which users can view and post jobs to this site.
              </p>

              {siteAssignmentOptions.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No client users available for this client.
                </p>
              ) : (
                <>
                  <div className="mb-2 flex items-center justify-between text-sm">
                    <span className="text-gray-500">
                      {selectedOptionsCount} of {siteAssignmentOptions.length}{' '}
                      selected
                    </span>
                    <div className="flex items-center gap-3">
                      <Button
                        type="button"
                        variant="text"
                        onClick={selectAllAssignable}
                        className="px-0 py-0 font-medium"
                      >
                        Select all
                      </Button>
                      <Button
                        type="button"
                        variant="text"
                        onClick={clearSelection}
                        className="px-0 py-0 font-medium"
                      >
                        Clear
                      </Button>
                    </div>
                  </div>

                  <div className="max-h-64 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
                    {siteAssignmentOptions.map((option) =>
                      option.type === 'user' ? (
                        <label
                          key={`user-${option.id}`}
                          className="flex cursor-pointer items-start gap-3 px-4 py-3 hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            checked={selectedUserIds.includes(option.id)}
                            onChange={() => toggleSelectedUser(option.id)}
                            className="mt-0.5 h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="flex flex-col">
                            <span className="text-sm text-gray-900">
                              {getUserDisplayName(option.user) ? (
                                <div className="flex flex-col items-start gap-1">
                                  <span className="text-sm font-medium text-gray-900">
                                    {getUserDisplayName(option.user)}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {option.user.email}
                                  </span>
                                </div>
                              ) : (
                                <div className="flex flex-col items-start gap-1">
                                  <span className="mt-0.5 inline-flex w-fit items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                                    Invited
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {option.email}
                                  </span>
                                </div>
                              )}
                            </span>
                          </span>
                        </label>
                      ) : (
                        <label
                          key={`invite-${option.id}`}
                          className="flex cursor-pointer items-start gap-3 px-4 py-3 hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            checked={selectedInviteIds.includes(option.id)}
                            onChange={() => toggleSelectedInvite(option.id)}
                            className="mt-0.5 h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="flex flex-col">
                            <span className="mt-0.5 inline-flex w-fit items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                              Invited
                            </span>
                            <span className="text-xs text-gray-500">
                              {option.invite.email}
                            </span>
                          </span>
                        </label>
                      ),
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="flex flex-shrink-0 justify-end space-x-4 border-t px-8 py-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={formik.isSubmitting}
              className="px-4 py-2 text-sm font-medium"
            >
              {formik.isSubmitting ? 'Adding...' : 'Add Site'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SiteModal;
